/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import Uri from 'jsuri';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
// NOTE: this MUST be loaded and not imported because it BREAKS SSR
import createLoadableComponent from 'js/lib/createLoadableComponent';
import { useLocation } from 'js/lib/useRouter';

import { Button, Grid, Typography2, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { Props } from 'bundles/verification/components/withJumioVerification';
import withJumioVerification from 'bundles/verification/components/withJumioVerification';
import {
  ALL_RETRIES_USED,
  APPROVED,
  NOT_STARTED,
  PENDING,
  RETRY_LIMIT,
  getIdStateMessages,
  getNameVerificationMessages,
  getVerificationTitles,
} from 'bundles/verification/data/VerificationConstants';
import type { IdStateMessage } from 'bundles/verification/data/VerificationConstants';
import { getUserFullName } from 'bundles/verification/data/VerificationUtils';

import _t from 'i18n!nls/verification';

const PersonaVerificationModal = createLoadableComponent(
  () => import('bundles/verification/components/PersonaVerificationModal')
);

const styles = {
  row: css`
    gap: var(--cds-spacing-200);
    align-items: flex-start;
    flex-wrap: wrap;

    ${breakpoints.down('xs')} {
      gap: var(--cds-spacing-100);
    }
  `,
  ctaColumn: css`
    padding-top: var(--cds-spacing-50);
  `,
  ctaButton: css`
    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: unset;
    }
  `,
  textColumn: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  notice: css`
    padding-top: var(--cds-spacing-150);
    color: var(--cds-color-grey-800);
  `,
  helpCenterLink: css`
    text-decoration: underline;
  `,
};

type CdsProps = Props & {
  'data-test'?: string;
};

export const JumioVerificationSettings = ({
  userVerificationInformation,
  latestVerificationStatus,
  verificationRequirements,
  refetchVerificationStatus,
  additionalQueryParams,
  openInNewTab,
  onVerificationComplete,
  'data-test': dataTest,
}: CdsProps) => {
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [personaFlowCompleted, setPersonaFlowCompleted] = useState(false);
  const hasModalBeenOpened = useRef(false);
  const track = useTracker();
  const {
    query: { verificationType },
  } = useLocation();

  useEffect(() => {
    if (verificationType === 'id') {
      setIsModalOpened(true);
    }
  }, [verificationType]);

  useEffect(() => {
    if (isModalOpened) {
      hasModalBeenOpened.current = true;
    } else if (hasModalBeenOpened.current) {
      hasModalBeenOpened.current = false;
      const button = document.getElementById('id_verification_button');
      if (button instanceof HTMLButtonElement) button.focus();
    }
  }, [isModalOpened]);

  const closeModal = useCallback(() => {
    setIsModalOpened(false);
    refetchVerificationStatus?.();
    onVerificationComplete?.();
  }, [refetchVerificationStatus, onVerificationComplete]);

  const personaOverrideOnSuccess = useCallback(() => {
    setPersonaFlowCompleted(true);
    setIsModalOpened(false);
    onVerificationComplete?.();
  }, [onVerificationComplete]);

  const handleVerifyIDClick = useCallback(() => {
    track('click_button', {
      button: {
        name: 'request_idv_start',
      },
    });
    setIsModalOpened(true);
  }, [track]);

  const handleNameAttestationClick = useCallback(() => {
    track('click_button', {
      button: { name: 'account_setting_name_attestation_btn' },
    });
  }, [track]);

  const shouldRenderIdVerification = () => {
    const { verifiedBy } = userVerificationInformation;
    // Note - if user verifiedBy SSO_SIGNIN, we should show name verification
    // flow. So this should output false.
    return (
      verificationRequirements === 'NEEDS_TO_VERIFY_ID' ||
      (verificationRequirements === 'REQUIREMENT_MET' && ['JUMIO', 'ADMIN', 'SIGTRACK', 'PERSONA'].includes(verifiedBy))
    );
  };

  const getIdVerificationState = () => {
    const { verifiedBy, jumioIdVerificationAttemptHistory } = userVerificationInformation;
    const { failedAttemptCount } = !!jumioIdVerificationAttemptHistory && jumioIdVerificationAttemptHistory;

    if (failedAttemptCount === 0 && ['JUMIO', 'SSO_SIGNIN', 'SIGTRACK', 'ADMIN', 'PERSONA'].includes(verifiedBy)) {
      return APPROVED;
    }
    if (failedAttemptCount >= RETRY_LIMIT) {
      // Jumio specific consideration
      return ALL_RETRIES_USED;
    } else if (!latestVerificationStatus) {
      return NOT_STARTED;
    }
    return latestVerificationStatus.verificationStatus;
  };

  const renderIdCta = (ctaText: string) => (
    <Button
      id="id_verification_button"
      data-testid="id-verification-button"
      disabled={personaFlowCompleted}
      variant="secondary"
      size="medium"
      onClick={handleVerifyIDClick}
      aria-label={getVerificationTitles().ID_VERIFICATION_TITLE}
      loading={isModalOpened}
      css={styles.ctaButton}
    >
      {ctaText}
    </Button>
  );

  const renderNameCta = (ctaText: string) => {
    const userAttestationPageUrl = new Uri().setPath('/user-verification');

    Object.entries(additionalQueryParams ?? {}).forEach(([paramName, paramValue]) =>
      userAttestationPageUrl.addQueryParam(paramName, paramValue)
    );

    return (
      <Button
        variant="secondary"
        size="medium"
        component="a"
        href={userAttestationPageUrl.toString()}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        onClick={handleNameAttestationClick}
        css={styles.ctaButton}
      >
        {ctaText}
      </Button>
    );
  };

  const renderIdVerification = () => {
    const verificationState = getIdVerificationState();
    const fullName = getUserFullName(userVerificationInformation);
    const idStateMessages = getIdStateMessages(fullName);
    const stateMessage = idStateMessages[verificationState] as IdStateMessage | undefined;
    const contentDivText = stateMessage?.contentDivText;
    const ctaDivText = stateMessage?.ctaDivText;

    const title = getVerificationTitles().ID_VERIFICATION_TITLE;
    const ctaText = verificationState === PENDING ? _t('Update my ID') : _t('Verify my ID');
    const ctaDiv = ctaDivText ? renderIdCta(ctaText) : null;

    const contentDiv = (
      <Typography2 component="div" variant="bodySecondary" color="supportText">
        {isModalOpened && <PersonaVerificationModal closeModal={closeModal} onSuccessFunc={personaOverrideOnSuccess} />}
        {contentDivText}
      </Typography2>
    );

    const noticeDiv = ctaDivText ? (
      <Typography2 component="p" variant="bodyTertiary" css={styles.notice} data-testid="id-verification-notice">
        <FormattedMessage
          message={_t(
            'Note: Upon clicking {ctaDivText}, you will be taken to a third party service "Persona" to securely complete the verification process. If you need any assistance completing the ID verification, please visit our {helpCenter}'
          )}
          helpCenter={
            <a
              href="https://learner.coursera.help/hc/articles/209818953-Set-up-ID-verification"
              target="_blank"
              rel="noopener noreferrer"
              css={styles.helpCenterLink}
            >
              {_t('help center')}
            </a>
          }
          ctaDivText={ctaText}
        />
      </Typography2>
    ) : null;

    return { title, contentDiv, ctaDiv, noticeDiv };
  };

  const renderNameAttestation = () => {
    const title = _t('Name verification');
    const fullName = getUserFullName(userVerificationInformation);
    const { VERIFY_NAME, UPDATE_NAME } = getNameVerificationMessages(fullName);
    const messages = userVerificationInformation.verifiedBy !== 'NONE' ? UPDATE_NAME : VERIFY_NAME;
    const ctaText =
      userVerificationInformation.verifiedBy !== 'NONE' ? _t('Request name change') : _t('Verify my name');

    return {
      title,
      contentDiv: (
        <Typography2 component="div" variant="bodySecondary" color="supportText">
          {messages.contentDivText}
        </Typography2>
      ),
      ctaDiv: renderNameCta(ctaText),
      noticeDiv: null,
    };
  };

  const { title, contentDiv, ctaDiv, noticeDiv } = shouldRenderIdVerification()
    ? renderIdVerification()
    : renderNameAttestation();

  return (
    <div data-e2e="verification" data-test={dataTest}>
      <Grid container css={styles.row}>
        <Grid item xs css={styles.textColumn}>
          <Typography2 component="h2" variant="subtitleLarge">
            {title}
          </Typography2>
          {contentDiv}
          {noticeDiv}
        </Grid>
        {ctaDiv && (
          <Grid item xs={12} sm="auto" css={styles.ctaColumn}>
            {ctaDiv}
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default withJumioVerification(JumioVerificationSettings);
